import React, { useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Button, Flex } from "antd"
import { routes } from "router"
import { BOOKING_MODES } from "pagesV2/pro/Bookings/constants"
import { getApiErrorMessage } from "helpers/embedded"
import {
  addToWidgetCart,
  changeWidgetCartItemChoice,
  fetchExperienceBookingDetails,
  hasCartError,
  resetBookingSelections,
  updateBookingSelections
} from "store/experiences"
import { countDays, getFixedDurationOptions, isDynamicMultiDay, isFixedMultiDay } from "utils/multiDayPricing"
import { getMultiDaySpan, getMultiDayStartTimes, normalizeTimeSlots } from "utils/experienceAvailability"
import { buildPanelSummary } from "../pricing"
import { describeAvailabilityRefusal } from "../../CartHold/holdIssue"
import ReservationExpiredNotice from "../../CartHold/ReservationExpiredNotice"
import useCartHold from "../../CartHold/useCartHold"
import useCartItemBeingChanged from "./useCartItemBeingChanged"
import { CART_ERRORS } from "../../constants"
import { buildInitialCounts, getTotalGuests } from "../constants"
import DateSection from "./DateSection"
import DurationSection from "./DurationSection"
import StartTimeSection from "./StartTimeSection"
import GuestsSection from "./GuestsSection"
import PriceSummary from "./PriceSummary"
import PanelFrame from "./PanelFrame"
import styles from "./BookingPanel.module.css"

const NO_ERRORS = { date: null, duration: null, time: null, guests: null }

// "Real-Time Availability" panel for a private experience: date -> duration -> start time -> guests,
// with the live price. A fixed multi-day trip picks its duration first (one card per trip price,
// when it has several): the start date then covers that many days, and only the start dates the
// API offers for that duration can be picked. A dynamic multi-day trip picks a start and an end
// date instead (endDate), within its shortest and longest stay and the longest stay the API allows
// from that start. A multi-day trip's start times come from its availability schedules. The
// choices are a draft in the shared
// bookingSelections store; Add to Cart and Book Now add a copy to the cart (Book Now then skips
// the cart page), so browsing other experiences never changes what is in the cart. Opened from a
// cart item's "Choose another time" it starts from that item's choices and Save Changes changes it.
const BookingPanel = ({ experience, isSheetOpen, onSheetClose }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { uuid, id } = useParams()
  const { bookingData, bookingSelections } = useSelector((state) => state.experiences.experienceBookingData)
  const changing = useCartItemBeingChanged()
  const [slots, setSlots] = useState([])
  const [isSlotsLoading, setIsSlotsLoading] = useState(false)
  const [errors, setErrors] = useState(NO_ERRORS)
  const [isAdding, setIsAdding] = useState(false)
  // Why Add to Cart / Book Now was refused, shown by the buttons until the choice changes.
  const [addError, setAddError] = useState(null)
  // Only the latest slots request may update the chips (a quick date change can overtake a slow one).
  const slotsRequestRef = useRef(0)
  // A multi-day trip price's availability, from the API: { tripPriceId, dates (the bookable start
  // dates), bookedDates, blockedReasons, maxSpanByDate }.
  const [availability, setAvailability] = useState(null)
  const availabilityRequestRef = useRef(0)

  const durations = bookingData?.trip_durations || []
  const minGuests = bookingData?.min_guests ?? experience?.min_guests ?? 0
  const maxGuests = bookingData?.max_guests ?? experience?.max_guests ?? 0
  const { date, endDate, tripLength, timeSlot, counts } = bookingSelections
  const priceData = tripLength?.price || null
  const isMultiDay = experience?.duration_type === "multi_day"
  const isFixedDays = isFixedMultiDay(experience)
  const isDynamicDays = isDynamicMultiDay(experience)
  const minDays = Number(experience?.min_duration_days) || 0
  const maxDays = Number(experience?.max_duration_days) || 0
  const fixedDurationOptions = useMemo(() => (isFixedDays ? getFixedDurationOptions(experience) : []), [isFixedDays, experience])
  const multiDayDurations = useMemo(
    () =>
      fixedDurationOptions.map((option) => ({
        key: option.key,
        value: t("booking_widget.experience.duration_days", { count: option.days }),
        price: option.price
      })),
    [fixedDurationOptions, t]
  )
  // A fixed trip covers the chosen duration's days (the experience's duration_value only describes
  // records from before trips had several durations).
  const selectedFixedDays = fixedDurationOptions.find((option) => option.key === tripLength?.key)?.days
  // A dynamic trip covers its chosen days, or its shortest stay until both dates are chosen.
  let spanDays = 1
  if (isDynamicDays) spanDays = date && endDate ? countDays(date, endDate) : Math.max(minDays, 1)
  else if (isMultiDay) spanDays = selectedFixedDays || fixedDurationOptions[0]?.days || getMultiDaySpan(experience)
  const tripAvailability = availability && availability.tripPriceId === tripLength?.key ? availability : null
  const bookableStartDates = tripAvailability?.dates || null

  const loadSlots = (nextDate, duration) => {
    const requestId = ++slotsRequestRef.current
    setSlots([])
    if (isMultiDay) {
      setIsSlotsLoading(false)
      setSlots(getMultiDayStartTimes(experience, nextDate))
      return
    }
    if (!nextDate || !duration?.key) {
      setIsSlotsLoading(false)
      return
    }
    setIsSlotsLoading(true)
    dispatch(fetchExperienceBookingDetails(id, "available_slots", duration.key, nextDate, undefined, BOOKING_MODES.embedded))
      .then((response) => {
        if (requestId === slotsRequestRef.current) setSlots(normalizeTimeSlots(response?.data))
      })
      .catch(() => {})
      .finally(() => {
        if (requestId === slotsRequestRef.current) setIsSlotsLoading(false)
      })
  }

  const selectDuration = (duration, nextDate = date) => {
    const hasGuests = getTotalGuests(counts) > 0
    dispatch(
      updateBookingSelections({
        tripLength: duration,
        timeSlot: null,
        counts: hasGuests ? counts : buildInitialCounts(duration?.price, minGuests)
      })
    )
    setErrors((prev) => ({ ...prev, duration: null, time: null }))
    loadSlots(nextDate, duration)
  }

  // Coming back to the same experience keeps the draft; another experience starts a new one (the
  // cart can hold this experience more than once, so it is not a draft source). Changing a cart item
  // starts from its choices. Slots for a kept date + duration are reloaded.
  useEffect(() => {
    if (!id) return
    if (changing.selection) {
      dispatch(resetBookingSelections())
      dispatch(updateBookingSelections({ ...changing.selection, experienceId: id }))
    }
    const kept = changing.selection || (bookingSelections.experienceId === id ? bookingSelections : null)
    if (!kept) {
      dispatch(resetBookingSelections())
      dispatch(updateBookingSelections({ experienceId: id }))
    }
    dispatch(fetchExperienceBookingDetails(id, "trip_data", "", "", undefined, BOOKING_MODES.embedded)).catch(() => {})
    if (kept) loadSlots(kept.date, kept.tripLength)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, changing.cartId, dispatch])

  // A single duration needs no choice.
  useEffect(() => {
    if (isMultiDay) return
    if (durations.length === 1 && tripLength?.key !== durations[0].key) selectDuration(durations[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durations, isMultiDay])

  // A fixed multi-day trip opens on its shortest duration, so the calendar knows how many days to
  // shade; any other multi-day trip prices from its first trip price, as the old widget does. A
  // duration already chosen (a kept draft or a cart item being changed) is kept.
  useEffect(() => {
    if (!isMultiDay) return
    if (tripLength?.key != null && experience?.trip_prices?.some((price) => price.id === tripLength.key)) return
    const seed = isFixedDays ? multiDayDurations[0] : experience?.trip_prices?.[0] && { key: experience.trip_prices[0].id, value: "", price: experience.trip_prices[0] }
    if (seed) selectDuration(seed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiDay, isFixedDays, multiDayDurations, experience?.trip_prices])

  // The start dates a multi-day trip price can be booked from: the API checks that every day of the
  // trip is free (no booking, blackout or day off, inside the season), and for a dynamic trip how
  // long a stay can run from each one.
  useEffect(() => {
    if (!isMultiDay || !id || tripLength?.key == null) return
    const requestId = ++availabilityRequestRef.current
    const tripPriceId = tripLength.key
    dispatch(fetchExperienceBookingDetails(id, "available_slots", tripPriceId, "", undefined, BOOKING_MODES.embedded))
      .then((response) => {
        const data = response?.data
        if (requestId !== availabilityRequestRef.current || !Array.isArray(data?.available_dates)) return
        setAvailability({
          tripPriceId,
          dates: data.available_dates,
          bookedDates: data.booked_dates || [],
          blockedReasons: data.blocked_reasons || {},
          maxSpanByDate: data.max_span_by_date || {}
        })
      })
      .catch(() => {})
  }, [isMultiDay, id, tripLength?.key, dispatch])

  // A start date that can't start the trip (chosen for another duration, or no longer free) is
  // cleared, with a note; so is a dynamic trip's end past the longest stay from its start.
  useEffect(() => {
    if (!bookableStartDates || !date) return
    if (!bookableStartDates.includes(date)) {
      dispatch(updateBookingSelections({ date: null, endDate: null, timeSlot: null }))
      setSlots([])
      setErrors((prev) => ({
        ...prev,
        date: isDynamicDays
          ? t("booking_widget.experience.errors.start_date_gone")
          : t("booking_widget.experience.errors.start_date_unavailable", { count: spanDays })
      }))
      return
    }
    const longestStay = Math.min(Number(tripAvailability?.maxSpanByDate?.[date]) || maxDays, maxDays || Infinity)
    if (isDynamicDays && endDate && longestStay > 0 && countDays(date, endDate) > longestStay) {
      dispatch(updateBookingSelections({ endDate: null }))
      setErrors((prev) => ({ ...prev, date: t("booking_widget.experience.errors.end_date") }))
    }
  }, [bookableStartDates, tripAvailability, date, endDate, isDynamicDays, maxDays, spanDays, dispatch, t])

  // A single start time needs no choice either.
  useEffect(() => {
    if (slots.length === 1 && !timeSlot) dispatch(updateBookingSelections({ timeSlot: slots[0] }))
  }, [slots, timeSlot, dispatch])

  const handleDateChange = (nextDate) => {
    dispatch(updateBookingSelections({ date: nextDate, timeSlot: null }))
    setErrors((prev) => ({ ...prev, date: null, time: null }))
    loadSlots(nextDate, tripLength)
  }

  // A dynamic trip's start and end. Start times follow the start date.
  const handleRangeChange = (nextStart, nextEnd) => {
    const isNewStart = nextStart !== date
    dispatch(updateBookingSelections({ date: nextStart, endDate: nextEnd, ...(isNewStart && { timeSlot: null }) }))
    setErrors((prev) => ({ ...prev, date: null, ...(isNewStart && { time: null }) }))
    if (isNewStart) loadSlots(nextStart, tripLength)
  }

  const handleTimeChange = (slot) => {
    dispatch(updateBookingSelections({ timeSlot: slot }))
    setErrors((prev) => ({ ...prev, time: null }))
  }

  const handleGuestsChange = (nextCounts) => {
    dispatch(updateBookingSelections({ counts: nextCounts }))
    setErrors((prev) => ({ ...prev, guests: null }))
  }

  // A dynamic trip is priced once both its dates are chosen (its price is per day).
  const isPriceable = !!priceData && getTotalGuests(counts) > 0 && (!isDynamicDays || !!endDate)
  const summary = useMemo(
    () => (isPriceable ? buildPanelSummary({ experience, priceData, date, endDate, counts }) : null),
    [isPriceable, experience, priceData, date, endDate, counts]
  )

  const validate = () => {
    const requiredGuests = Math.max(1, minGuests || 0)
    const nextErrors = {
      date: (date ? null : t("booking_widget.experience.errors.date")) || (isDynamicDays && !endDate ? t("booking_widget.experience.errors.end_date") : null),
      duration: tripLength ? null : t("booking_widget.experience.errors.duration"),
      // A multi-day trip without a scheduled start time on that day can still be booked.
      time: timeSlot || (isMultiDay && date && slots.length === 0) ? null : t("booking_widget.experience.errors.time"),
      guests: getTotalGuests(counts) >= requiredGuests ? null : t("booking_widget.experience.errors.guests", { count: requiredGuests })
    }
    setErrors(nextErrors)
    return Object.values(nextErrors).every((error) => !error)
  }

  // A new choice starts without the last refusal.
  useEffect(() => {
    setAddError(null)
  }, [date, endDate, tripLength, timeSlot, counts])

  // While the cart's reservation is over nothing more is added until "Check availability & continue"
  // reserves it again (the API refuses, hold_expired).
  const { isReservationOver } = useCartHold()

  // Added to the cart with the priced amount, as the old widget does, once its places are held
  // (addToWidgetCart; the same date and start time changes the item already there). A start time
  // that's gone is refused, and the start times are loaded again.
  const handleContinue = async (path) => {
    if (isAdding || isReservationOver || !validate()) return
    setIsAdding(true)
    try {
      const selection = { ...bookingSelections, isMultiDay, ...(summary && { amount: summary.total }) }
      await dispatch(changing.cartId ? changeWidgetCartItemChoice(changing.cartId, selection) : addToWidgetCart(selection))
      navigate(path)
    } catch (error) {
      setIsAdding(false)
      // The cart was read again and now shows the expired notice
      if (hasCartError(error, CART_ERRORS.holdExpired)) return
      const warning = describeAvailabilityRefusal({ message: error?.data?.message, experience, t })
      setAddError(
        warning
          ? `${t("booking_widget.experience.availability_changed")}: ${warning}`
          : getApiErrorMessage(error) || t("booking_widget.checkout.errors.generic")
      )
      if (warning) loadSlots(date, tripLength)
    }
  }

  const actions = (
    <>
      {addError && (
        <span className={styles.errorText} role="alert">
          {addError}
        </span>
      )}
      {isReservationOver && <ReservationExpiredNotice />}
      {changing.cartId && <span className={styles.paymentNote}>{t("booking_widget.experience.changing_item")}</span>}
      <Button
        type="primary"
        shape="round"
        size="large"
        block
        loading={isAdding}
        disabled={isReservationOver}
        onClick={() => handleContinue(routes.widgetCart({ uuid }))}
      >
        {t(changing.cartId ? "booking_widget.experience.save_changes" : "booking_widget.experience.add_to_cart")}
      </Button>
      {!changing.cartId && (
        <Button
          shape="round"
          size="large"
          block
          color="primary"
          variant="outlined"
          disabled={isAdding || isReservationOver}
          onClick={() => handleContinue(routes.widgetCheckout({ uuid }))}
        >
          {t("booking_widget.experience.book_now")}
        </Button>
      )}
      <span className={styles.paymentNote}>{t("booking_widget.experience.payment_note")}</span>
    </>
  )

  return (
    <PanelFrame isSheetOpen={isSheetOpen} onSheetClose={onSheetClose} actions={actions}>
      {isMultiDay && (
        <Flex vertical gap={2} className={styles.multiDayBanner}>
          <span className={styles.multiDayBannerTitle}>
            {isDynamicDays && minDays && maxDays
              ? t("booking_widget.experience.multi_day_range_title", { min: minDays, max: maxDays })
              : t("booking_widget.experience.multi_day_title", { count: spanDays })}
          </span>
          <span>{t(isDynamicDays ? "booking_widget.experience.multi_day_range_hint" : "booking_widget.experience.multi_day_hint")}</span>
        </Flex>
      )}
      {/* Keyed by the draft's experience: the calendar reopens on the right month once this
          experience's draft replaces the previous one. */}
      {multiDayDurations.length > 1 && (
        <DurationSection
          experience={experience}
          durations={multiDayDurations}
          selectedKey={tripLength?.key}
          date={date}
          onSelect={(duration) => selectDuration(duration)}
          error={errors.duration}
        />
      )}
      <DateSection
        key={bookingSelections.experienceId}
        experience={experience}
        value={date}
        onChange={handleDateChange}
        error={errors.date}
        spanDays={spanDays}
        bookableDates={bookableStartDates}
        isRange={isDynamicDays}
        endValue={endDate || null}
        onRangeChange={handleRangeChange}
        minDays={minDays}
        maxDays={maxDays}
        maxSpanByDate={tripAvailability?.maxSpanByDate}
        bookedDates={tripAvailability?.bookedDates}
        blockedReasons={tripAvailability?.blockedReasons}
      />
      {!isMultiDay && durations.length > 1 && (
        <DurationSection
          experience={experience}
          durations={durations}
          selectedKey={tripLength?.key}
          date={date}
          onSelect={(duration) => selectDuration(duration)}
          error={errors.duration}
        />
      )}
      <StartTimeSection
        slots={slots}
        selectedSlot={timeSlot}
        isLoading={isSlotsLoading}
        hasDate={!!date && !!tripLength}
        onSelect={handleTimeChange}
        error={errors.time}
      />
      {priceData && (
        <GuestsSection
          experience={experience}
          priceData={priceData}
          date={date}
          endDate={endDate || null}
          counts={counts}
          minGuests={minGuests}
          maxGuests={maxGuests}
          onChange={handleGuestsChange}
          error={errors.guests}
        />
      )}
      {summary && <PriceSummary summary={summary} />}
    </PanelFrame>
  )
}

BookingPanel.propTypes = {
  experience: PropTypes.object,
  isSheetOpen: PropTypes.bool,
  onSheetClose: PropTypes.func
}

BookingPanel.defaultProps = {
  experience: null,
  isSheetOpen: false,
  onSheetClose: () => {}
}

export default BookingPanel
